import React from 'react';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '../../../common/components';
import { Ikey, LicenseBackend } from '../../types.type';
import { shortString, transformDateString } from '../../util';
import { managementService } from '../../service/management.service';
import { homeService } from '../../../home/service/home.service';
import { useThrottle } from '../../../hooks/useThrottle';
import { ReleaseButton } from '../release-button.component';

interface IProps {
  data: Ikey[];
}

// The desktop pill's behaviour, in the words this layout uses. Kept in step
// with SeatsBadge in management-table.component.tsx: both dispatch on the
// row's backend, and both only offer a top-up on a licence that can take one.
const MobileSeats = ({
  used,
  max,
  stripeId,
  row
}: {
  used: number;
  max: number;
  stripeId: string | null;
  row: Ikey;
}) => {
  const { t } = useTranslation();
  const squarebit = row.backend === 'squarebit';
  const topUpable = squarebit && row.status === 'active' && row.source === 'stripe';

  const updatePortal = useMutation({
    mutationKey: ['subscriptionUpdatePortal', stripeId, row.licenseId],
    mutationFn: () =>
      squarebit
        ? (homeService.getCheckoutSession(
            'perpetual',
            row.productSlug,
            { version: '', locale: '', price: '' },
            1,
            row.licenseId!
          ) as Promise<string>)
        : managementService.getSubscriptionUpdateUrl(stripeId!),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => toast.error(t('management.table.seats.updateError'))
  });
  const throttled = useThrottle(updatePortal.mutate);

  const label = `${used}/${max} ${t('management.table.seats.unit', { count: max })}`;

  if (squarebit ? !topUpable : !stripeId) {
    return <span>{label}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => throttled()}
      className="inline-flex items-center gap-[4px] text-blue font-semibold underline underline-offset-2 hover:text-blue-deep-1 cursor-pointer bg-transparent border-0 p-0"
    >
      {label}
    </button>
  );
};

//: The desktop badge's rules, in this layout's sizes. See TypeBadge in
//: management-table.component.tsx for why the namespace follows the backend.
const sourceColors: Record<string, string> = {
  stripe: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text'
};

const MobileTypeBadge = ({
  type,
  subscriptionStatus,
  cancelAtPeriodEnd,
  backend
}: {
  type: string;
  subscriptionStatus?: string | null;
  cancelAtPeriodEnd?: boolean;
  backend?: LicenseBackend;
}) => {
  const { t } = useTranslation();
  const isCancelled =
    backend !== 'squarebit' &&
    (cancelAtPeriodEnd || subscriptionStatus === 'canceled');

  if (isCancelled) {
    return (
      <span className="inline-block px-[7px] py-[2px] rounded-[4px] border bg-badge-red-bg border-badge-red-text/40 text-badge-red-text font-mono text-[9px] tracking-[0.06em] uppercase">
        {t('subscription.cancelled')}
      </span>
    );
  }

  const colors: Record<string, string> = {
    trial: 'bg-badge-red-bg/60   border-badge-red-text/30   text-badge-red-text',
    monthly: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text',
    yearly: 'bg-badge-green-bg/60 border-badge-green-text/30 text-badge-green-text'
  };
  const palette = backend === 'squarebit' ? sourceColors : colors;
  const style =
    palette[type.toLowerCase()] ?? 'bg-gray-100 border-gray-300 text-gray-600';
  const words =
    backend === 'squarebit'
      ? `management.table.tier.${type.toLowerCase()}`
      : `subscription.licenseType.${type.toLowerCase()}`;
  return (
    <span
      className={`inline-block px-[7px] py-[2px] rounded-[4px] border font-mono text-[9px] tracking-[0.06em] uppercase ${style}`}
    >
      {t(words, { defaultValue: type })}
    </span>
  );
};

export const ManagementTableMobile = ({ data }: IProps) => {
  const { t } = useTranslation();

  return (
    <div className="w-full">
      <div className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-content-muted-light mb-[13px]">
        {t('management.table.heading')}
      </div>

      <div className="flex flex-col gap-[8px]">
        {data.map((row) => {
          const {
            key,
            productName,
            type,
            expiresAt,
            maxActivations,
            usedActivations,
            subscriptionStatus,
            cancelAtPeriodEnd,
            stripeId
          } = row;
          // A perpetual licence has no date. Empty here read as a missing
          // value rather than as the absence of an expiry.
          const date = expiresAt
            ? transformDateString(expiresAt)
            : t('management.table.perpetual');
          const keyShort = shortString(key);

          return (
            <div
              key={key}
              className="flex items-center justify-between gap-[10px] px-[14px] py-[13px] bg-surface-section border border-border-dark/[0.11] rounded-[10px]"
            >
              <div className="flex flex-col gap-[4px] min-w-0">
                <div className="flex items-center gap-[6px] flex-wrap">
                  <span className="text-[12px] font-semibold text-content-secondary truncate">
                    {productName}
                  </span>
                  <span className="font-mono text-[12px] tracking-[0.05em] text-content-muted-alt">
                    {keyShort}
                  </span>
                  <MobileTypeBadge
                    type={type}
                    subscriptionStatus={subscriptionStatus}
                    cancelAtPeriodEnd={cancelAtPeriodEnd}
                    backend={row.backend}
                  />
                </div>
                <span className="text-[12px] text-content-muted-alt">
                  {date} ·{' '}
                  <MobileSeats
                    used={usedActivations}
                    max={maxActivations}
                    stripeId={stripeId}
                    row={row}
                  />
                </span>
              </div>
              <div className="flex items-center gap-[6px] shrink-0">
                <ReleaseButton row={row} className="shrink-0" />
                <button
                  className="w-[32px] h-[32px] rounded-[8px] bg-surface-base border border-border-dark/[0.11] flex items-center justify-center cursor-pointer text-content-muted-alt shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all hover:text-blue hover:border-blue hover:bg-surface-hover shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(key);
                    toast.success(t('management.table.copySuccess'));
                  }}
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
